import { ArrowSquareOutIcon, XIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { useCallback, useState } from 'react'
import { match, P } from 'ts-pattern'
import { DRAWER_SLIDE_ANIMATION_DURATION_MS } from '../../components/drawers/drawer-constants.ts'
import { Spinner } from '../../components/spinner.tsx'
import { useGetRepositoryThreatModelRefreshRequest } from '../../utils/api-client/api-hooks'
import type {
  RefreshRequestResponse,
  RefreshRequestStateResponse,
  RefreshTriggerSourceResponse,
  ThreatModelStatus,
} from '../../utils/api-client/api-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import * as styles from './refresh-request-drawer.css.ts'
import { RefreshStateBadge } from './refresh-state-badge.tsx'

const getTriggerLabel = (trigger: RefreshTriggerSourceResponse, pullRequestNumber?: number): string =>
  match(trigger)
    .with('onboarding', () => 'Repository added')
    .with('direct-push', () => 'Push to default branch')
    .with('merged-pr', () => (pullRequestNumber ? `PR #${pullRequestNumber} merged` : 'Merged pull request'))
    .exhaustive()

// The row's `state` and the live `threat_model_document_status` are updated on different
// schedules — the doc status is dereferenced live from analysis-service at request time, whereas
// `state` is persisted and transitions via a separate notification path. Once the live status
// reports a terminal value, treat that as authoritative on the badge so the drawer never shows
// "Regenerating" next to "Regeneration completed" during the catch-up window.
const reconcileState = (
  state: RefreshRequestStateResponse,
  documentStatus: ThreatModelStatus | undefined
): RefreshRequestStateResponse =>
  match([state, documentStatus] as const)
    .with(['regenerating', 'completed'], () => 'completed' as const)
    .with(['regenerating', P.union('failed', 'abandoned')], () => 'failed' as const)
    .otherwise(() => state)

const getDocumentStatusLabel = (status: ThreatModelStatus, triggerSource: RefreshTriggerSourceResponse): string => {
  const isInitialGeneration = triggerSource === 'onboarding'
  return (
    match(status)
      .with('queued', () => (isInitialGeneration ? 'Generation queued' : 'Regeneration queued'))
      .with('processing', () => (isInitialGeneration ? 'Generating in progress' : 'Regenerating in progress'))
      .with('completed', () => (isInitialGeneration ? 'Generation completed' : 'Regeneration completed'))
      .with('failed', () => (isInitialGeneration ? 'Generation failed' : 'Regeneration failed'))
      .with('abandoned', () => (isInitialGeneration ? 'Generation never started' : 'Regeneration never started'))
      // `unknown` is analysis-service's escape hatch when it can't determine the live status —
      // present the same "unavailable — will retry" message as the no-status-yet path.
      .with('unknown', () => 'Live status unavailable — will retry.')
      .exhaustive()
  )
}

type RefreshRequestDrawerProps = {
  repositoryId: string
  initialRequest: RefreshRequestResponse
  onClose: () => unknown
}

// The list endpoint doesn't populate `threat_model_document_status`; the single-item GET does a
// best-effort live dereference against analysis-service for regenerating rows. Fetch it here so
// the drawer shows the freshest data while falling back to the list row when the fetch is idle.
export const RefreshRequestDrawer: React.FC<RefreshRequestDrawerProps> = ({
  repositoryId,
  initialRequest,
  onClose,
}) => {
  const [isClosing, setIsClosing] = useState(false)

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setIsClosing(true)
        setTimeout(onClose, DRAWER_SLIDE_ANIMATION_DURATION_MS)
      }
    },
    [onClose]
  )

  const { data: liveRequest } = useGetRepositoryThreatModelRefreshRequest({
    repositoryId,
    requestId: initialRequest.id,
  })

  const request = liveRequest ?? initialRequest
  const isLiveLoading = liveRequest === undefined
  const effectiveState = reconcileState(request.state, request.threat_model_document_status)
  const shortSha = request.after_sha?.slice(0, 7)
  const commitLink = request._links.commit?.href
  const pullRequestLink = request._links['pull-request']?.href

  return (
    <Dialog.Root open={true} modal={false} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Content
          className={isClosing ? styles.drawerContentClosing : styles.drawerContent}
          onInteractOutside={event => event.preventDefault()}
          aria-label="Update details"
        >
          <VisuallyHidden asChild>
            <Dialog.Title>Update details</Dialog.Title>
          </VisuallyHidden>
          <button
            type="button"
            className={styles.closeButton}
            aria-label="Close details"
            onClick={() => handleOpenChange(false)}
          >
            <XIcon size={24} weight="regular" />
          </button>

          <header className={styles.header}>
            <h2 className={styles.title}>{getTriggerLabel(request.trigger_source, request.pull_request_number)}</h2>
            <div className={styles.headerRow}>
              <RefreshStateBadge state={effectiveState} triggerSource={request.trigger_source} />
              <span className={styles.metaValue}>
                {formatDate({ date: request.triggered_at, type: 'mm/dd/yyyy-hh:mm' })}
              </span>
            </div>
          </header>

          <section className={styles.section}>
            <h3 className={styles.sectionHeading}>Details</h3>
            <dl className={styles.metaGrid}>
              {request.ref && (
                <>
                  <dt className={styles.metaLabel}>Ref</dt>
                  <dd className={styles.monoValue}>{request.ref}</dd>
                </>
              )}
              {shortSha && (
                <>
                  <dt className={styles.metaLabel}>Commit</dt>
                  <dd>
                    <CommitValue shortSha={shortSha} commitLink={commitLink} />
                  </dd>
                </>
              )}
              {request.before_sha && (
                <>
                  <dt className={styles.metaLabel}>Previous commit</dt>
                  <dd className={styles.monoValue}>{request.before_sha.slice(0, 7)}</dd>
                </>
              )}
              {request.trigger_source === 'merged-pr' && request.pull_request_number !== undefined && (
                <>
                  <dt className={styles.metaLabel}>Pull request</dt>
                  <dd>
                    <PullRequestValue number={request.pull_request_number} pullRequestLink={pullRequestLink} />
                  </dd>
                </>
              )}
            </dl>
          </section>

          {effectiveState === 'regenerating' && (
            <section className={styles.section}>
              <h3 className={styles.sectionHeading}>Live status</h3>
              <RegeneratingStatus
                documentStatus={request.threat_model_document_status}
                isLiveLoading={isLiveLoading}
                triggerSource={request.trigger_source}
              />
            </section>
          )}

          {request.decision_rationale && (
            <section className={styles.section}>
              <h3 className={styles.sectionHeading}>Rationale</h3>
              <p className={styles.rationaleBlock}>{request.decision_rationale}</p>
            </section>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

type CommitValueProps = {
  shortSha: string
  commitLink: string | undefined
}

const CommitValue: React.FC<CommitValueProps> = ({ shortSha, commitLink }) => {
  if (!commitLink) {
    return <span className={styles.monoValue}>{shortSha}</span>
  }
  return (
    <a href={commitLink} target="_blank" rel="noopener noreferrer" className={styles.inlineLink}>
      <span className={styles.monoValue}>{shortSha}</span>
      <ArrowSquareOutIcon size={14} weight="regular" />
    </a>
  )
}

type PullRequestValueProps = {
  number: number
  pullRequestLink: string | undefined
}

const PullRequestValue: React.FC<PullRequestValueProps> = ({ number, pullRequestLink }) => {
  if (!pullRequestLink) {
    return <span className={styles.metaValue}>#{number}</span>
  }
  return (
    <a href={pullRequestLink} target="_blank" rel="noopener noreferrer" className={styles.inlineLink}>
      <span>#{number}</span>
      <ArrowSquareOutIcon size={14} weight="regular" />
    </a>
  )
}

type RegeneratingStatusProps = {
  documentStatus: ThreatModelStatus | undefined
  isLiveLoading: boolean
  triggerSource: RefreshTriggerSourceResponse
}

const RegeneratingStatus: React.FC<RegeneratingStatusProps> = ({ documentStatus, isLiveLoading, triggerSource }) => {
  if (documentStatus) {
    return (
      <div className={styles.documentStatusRow}>
        <span className={styles.metaValue}>{getDocumentStatusLabel(documentStatus, triggerSource)}</span>
      </div>
    )
  }
  if (isLiveLoading) {
    return (
      <div className={styles.documentStatusRow}>
        <Spinner size="16" />
        <span className={styles.metaValue}>Fetching live status…</span>
      </div>
    )
  }
  // Absent status: analysis-service unreachable, or the row's document is no longer the
  // repository's "latest". Guide says treat absence as "unknown; retry".
  return <span className={styles.metaValue}>Live status unavailable — will retry.</span>
}
